<script setup lang="ts">
  import { computed, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import RadioQC from '@qicgroupdoha/qic-connect-ds/components/RadioQC'
  import { paymentMap } from '@/shared/constants/other.constants'
  import { PaymentType } from '@/shared/types/response.types'

  const PAYMENT_METHOD_BY_APPLE_PAY = 2

  interface Props {
    availablePaymentMethods: PaymentType[]
    googlePayAvailable: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    availablePaymentMethods: () => [],
  })

  const paymentMethod = defineModel<number>({ required: true })

  const { t } = useI18n()

  const isAvailablePaymentMethod = (methodType: PaymentType) =>
    props.availablePaymentMethods.includes(methodType)

  const isAppleOS = () => {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent || ''
    return /Mac|iPhone|iPad|iPod/i.test(ua)
  }

  const shouldDisplayAppleButton = () => {
    if (import.meta.env.MODE === 'development') {
      return isAvailablePaymentMethod(paymentMap.apple)
    }
    return (
      window.ApplePaySession &&
      window.ApplePaySession.canMakePayments() &&
      isAvailablePaymentMethod(paymentMap.apple)
    )
  }

  const shouldDisplayGoogleButton = () =>
    props.googlePayAvailable && isAvailablePaymentMethod(paymentMap.google)

  const paymentMethods = computed(() => [
    {
      value: 2,
      title: t('qic_quote_payment_apple'),
      icons: ['apple-pay'],
      isShown: shouldDisplayAppleButton(),
    },
    {
      value: 3,
      title: 'Google Pay',
      icons: ['google-pay'],
      isShown: shouldDisplayGoogleButton(),
    },
    {
      value: 0,
      title: t('qic_quote_payment_bank_card'),
      icons: ['visa', 'master-card', 'american-express'],
      isShown: isAvailablePaymentMethod(paymentMap.credit),
    },
    {
      value: 1,
      title: t('qic_quote_payment_naps'),
      icons: ['naps'],
      isShown: isAvailablePaymentMethod(paymentMap.naps),
    },
  ])

  const visiblePaymentMethods = computed(() =>
    paymentMethods.value.filter((m) => m.isShown)
  )

  watch(
    () => props.availablePaymentMethods,
    (newVal) => {
      if (!newVal) return
      if (isAppleOS() && shouldDisplayAppleButton()) {
        paymentMethod.value = PAYMENT_METHOD_BY_APPLE_PAY
      }
    },
    { immediate: true }
  )
</script>

<template>
  <div class="quote__checkout quote__checkout--payment">
    <div class="quote__method">
      <div class="quote__method-list">
        <div
          v-for="method in visiblePaymentMethods"
          :key="method.value"
          class="quote__method-item"
        >
          <!-- @vue-ignore -->
          <RadioQC
            v-model="paymentMethod"
            :container-attrs="{
              class: 'quote__radio',
              'data-test': `payment-method-${method.value}`,
            }"
            name="card"
            :value="method.value"
            :data-test="`payment-method-radio-${method.value}`"
          >
            <div class="quote__radio-inner">
              <span>{{ method.title }}</span>
              <div class="quote__radio-inner-icon">
                <!-- @vue-ignore -->
                <IconQC
                  v-for="iconName in method.icons"
                  :key="iconName"
                  :name="iconName"
                  size="lg"
                  :color="$qicConnect.colors.icn.srf.tertiary"
                />
              </div>
            </div>
          </RadioQC>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .quote__checkout {
    background: var(--colorBgSrfTertiary);
    border-radius: var(--radius-5);
  }

  .quote__checkout--payment {
    padding: 0;
  }

  .quote__method {
    margin: 0;
  }

  .quote__method-list {
    border: none;
    background: none;
    border-radius: 0;
  }

  .quote__method-radio + label {
    padding: 0;
  }

  .quote__method-item {
    padding: var(--spacing-5);
    line-height: 0;

    &:not(:last-child) {
      border-bottom: 1px solid var(--colorBrdrSrfPrimary);
    }
  }

  .quote__radio {
    width: 100%;
  }

  .quote__radio-inner {
    display: flex;
    justify-content: space-between;
  }

  .quote__radio-inner-icon {
    display: flex;
    gap: var(--spacing-3);
  }
</style>
